import { useQueryClient } from 'react-query'

import { apiPaths, queryKeys } from './config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  handleError,
  validateStatus,
} from 'api/kinerja/pengawas-sekolah/config'
import { useQueryApi, useMutationApi } from 'utils/api'

import type { AxiosError } from 'axios'
import type { UseQueryOptions } from 'react-query'
import type { CommonResponse } from 'types/api'
import type {
  PerencanaanFilterState,
  PerencanaanPS,
  TPerencanaanApprovalRequestBody,
  TPerencanaanApprovalRevisionRequestBody,
  TPerencanaanApprovalApproveRequestBody,
  TPerencanaanSummary,
} from 'types/kinerja-tim-penilai-ps'
import type {
  TPerencanaan,
  TPerencanaanSubmissionResponse,
} from 'types/kinerja-ps-pegawai'

export function usePerencanaanPSListSummary({
  opts,
}: {
  opts?: UseQueryOptions<TPerencanaanSummary>
}) {
  const { period } = usePeriodContext()

  return useQueryApi<TPerencanaanSummary>(
    apiPaths.perencanaanListSummary(period.id),
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
      },
      queryKey: [queryKeys.API_GET_LIST_SUMMARY_PERENCANAAN_PS, period.id],
    }
  )
}

export function useAPIGetListPerencanaanPS({
  filter = 'ALL',
  opts,
}: {
  filter?: PerencanaanFilterState
  opts?: UseQueryOptions<CommonResponse<PerencanaanPS[]>>
}) {
  const { period } = usePeriodContext()

  return useQueryApi<CommonResponse<PerencanaanPS[]>>(
    apiPaths.perencanaanList(period.id, filter),
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
      },
      queryKey: [queryKeys.API_GET_LIST_PERENCANAAN_PS, period.id, filter],
    }
  )
}

export function useAPIGetPerencanaanPSDetail({
  id,
  ...options
}: UseQueryOptions<TPerencanaan> & { id: string }) {
  return useQueryApi<TPerencanaan>(apiPaths.perencanaanDetail(id), {
    queryKey: [queryKeys.API_GET_PERENCANAAN_PS_DETAIL, id],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: true,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPISubmitApprovalSend(skpPsId: string) {
  const queryClient = useQueryClient()

  return useMutationApi<
    TPerencanaanSubmissionResponse,
    AxiosError,
    TPerencanaanApprovalRequestBody
  >(apiPaths.approvalSend, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_PS_DETAIL,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPISubmitApprovalRevision(skpPsId: string) {
  const queryClient = useQueryClient()

  return useMutationApi<
    TPerencanaanSubmissionResponse,
    AxiosError,
    TPerencanaanApprovalRevisionRequestBody
  >(apiPaths.approvalRevision, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_PS_DETAIL,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPISubmitApprovalApprove() {
  return useMutationApi<
    TPerencanaanSubmissionResponse,
    AxiosError,
    TPerencanaanApprovalApproveRequestBody
  >(apiPaths.approvalApprove, {
    axiosConfig: { method: 'POST' },
  })
}
